import { fetchVerification } from '@afterpaytouch/event-verify'
import { portalAction, PortalThunk } from '../types'
import { SupportedLocale } from '@afterpay/types'
import {
  AddEmailToRegistrationRequest,
  AddMobileToRegistrationRequest,
  ConfirmRegistrationRequest,
  CreateRegistration,
  CreateRegistrationRequest,
  CreateRegistrationResponse,
  Email,
  Mobile,
  RegistrationData,
  TransformedCreateRegistrationResponse,
  VerifyRegistrationAuthorizationRequest,
  VerifyRegistrationMobileEventRequest,
  VerifyRegistrationSMSAuthorizationResponse,
} from '@afterpaytouch/registration'
import { verifyServiceApi } from '../api'
import { Country, FinaliseAccountRequest } from '@afterpaytouch/portal-api'
import { VERIFY_SERVICE_BASE_URL } from '../request'
import { registrationSelector } from './selectors'
import { emailSelector } from '../account/account.hooks'
import { getApPortalToken } from '../../utils/tokenStorage'

/**
 * PORTAL REGISTRATION FLOW PATTERNS:
 *
 * ✅ Portal correctly calls emailLookup and sets verificationRequired in LoginIdentity
 *
 * 🔄 CURRENT PORTAL FLOW:
 * 1. LoginIdentity → emailLookup() sets auth.verificationRequired (true/false)
 * 2. SignupForm checks isAuthRequired (= auth.verificationRequired)
 * 3a. If false: updateRegistrationData() → finaliseAccountDetails() ✅ WORKS
 * 3b. If true: updateRegistrationData() → Route to VERIFY_SMS ✅ WORKS
 *
 * SMS VERIFICATION FLOW:
 * ✅ Portal stores form data and routes to VERIFY_SMS
 * ✅ confirmRegistrationMobile() action ready to use
 */

export const createRegistration: PortalThunk<CreateRegistration, TransformedCreateRegistrationResponse> = portalAction(
  'registration/new',
  async ({ flow, locale }: CreateRegistration, thunk) => {
    const email = emailSelector(thunk.getState())

    /*
      This is a temporary (hopefully) workaround for the purposes of email verification.
      Adding this header ensures that the email verification process will only run when the email is used to being registration rather than mobile.
     */
    const headers = {
      'X-Registration-Flow': flow,
    }
    const res = await thunk.extra
      .portalApi()
      .post<CreateRegistrationRequest, CreateRegistrationResponse>('/portal/consumers/registrations', { email, preferredLocale: locale }, headers)
    return { ...res, email }
  }
)

export const verifyRegistrationSMSAuthorization: PortalThunk<void, VerifyRegistrationSMSAuthorizationResponse> = portalAction(
  'registration/verify/mobile/sms/authz',
  async (_, thunk) => {
    const { id: registrationId, data } = registrationSelector(thunk.getState())

    const amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId() || 0

    const request: VerifyRegistrationAuthorizationRequest = {
      registrationId: registrationId ?? '',
      amplitudeSessionId,
      consumer: data as RegistrationData,
      transactionToken: getApPortalToken() ?? '',
    }

    try {
      const result = await verifyServiceApi
        .post<VerifyRegistrationAuthorizationRequest, VerifyRegistrationSMSAuthorizationResponse>('/authz/sms', request)
        .catch((e) => {
          return {
            code: e.response.data.code,
            message: e.response.data.message,
          }
        })
      return result
    } catch (e: unknown) {
      const error = e as { response?: { data?: { code?: string; message?: string } } }
      return {
        code: error?.response?.data?.code || ('FAILED' as const),
        message: error?.response?.data?.message || ('UNKNOWN' as const),
      }
    }
  }
)

export const verifyRegistrationMobileSMSEvent: PortalThunk<VerifyRegistrationMobileEventRequest, void> = portalAction(
  'registration/verify/mobile/sms/delay',
  async (verifyRegistrationMobileEventRequest, thunk) => {
    const portalToken = getApPortalToken() ?? ''
    const { onSuccess, onWaiting, onError } = verifyRegistrationMobileEventRequest

    return await fetchVerification({
      url: encodeURI(`${VERIFY_SERVICE_BASE_URL}/sms/${portalToken}`),
      onSuccess: () => {
        onSuccess()
      },
      onError: (d) => {
        onError(d)
      },
      onWaiting: (d) => {
        onWaiting(d)
      },
    })
  }
)

export const confirmRegistrationMobile: PortalThunk<string, void> = portalAction('registration/mobile', async (smsCode, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())

  const mobile: Mobile = data?.mobile ?? ''

  const request: AddMobileToRegistrationRequest = {
    registrationId: registrationId ?? '',
    mobile,
    smsCode,
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/mobile', request)
})

export const confirmRegistrationEmail: PortalThunk<string, void> = portalAction('registration/email', async (emailCode, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())

  const email: Email = data?.email ?? ''

  const request: AddEmailToRegistrationRequest = {
    registrationId: registrationId ?? '',
    email,
    emailCode,
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/email', request)
})

export const confirmRegistration: PortalThunk<void, void> = portalAction('registration/confirm', async (_, thunk) => {
  const { id: registrationId, data } = registrationSelector(thunk.getState())

  const preferredLocale = data?.preferredLocale ?? ('en-AU' as SupportedLocale)
  const marketingOptIn = data?.marketingOptIn ?? null

  const request: ConfirmRegistrationRequest = {
    registrationId: registrationId ?? '',
    origin: 'CONSUMER_PORTAL',
    preferredLocale,
    ...(marketingOptIn !== null ? { marketingPreference: marketingOptIn } : {}),
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/confirm', request)
})

export const finaliseAccountDetails: PortalThunk<void, void> = portalAction('registration/finaliseAccount', async (_, thunk) => {
  const { data } = registrationSelector(thunk.getState())
  if (data == null) {
    throw new Error('Registration data not found')
  }

  const { preferredLocale: _unused, ...finaliseAccountData } = data

  const request: FinaliseAccountRequest = {
    ...finaliseAccountData,
    countryCode: finaliseAccountData.countryCode as Country,
    creditPolicyAgree: false,
    providedCreditCheckConsent: finaliseAccountData.countryCode === 'NZ',
  }

  return await thunk.extra.portalApi().post('/portal/consumers/account/account-details', request)
})
